import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import * as Yup from "yup";

import CategoriesApi from "~/admin/apis/categories.api";
import ApiErrorsHelper from "~/admin/helpers/ApiErrorsHelper";
import useLoader from "~/hooks/useLoader";
import ValidationErrors from "~/admin/services/ValidationErrors";
import Button from "~/admin/components/Button";
import Input from "~/admin/components/Input";
import Label from "~/admin/components/Label";
import Modal from "~/admin/components/Modal";
import ValidationForm from "~/admin/components/ValidationForm";
import ImageUploader from "~/admin/components/ImageUploader";
import SubmitButton from "~/admin/components/SubmitButton";
import useDataLoading from "~/admin/hooks/useDataLoading";
import PageDataLoader from "~/admin/components/PageDataLoader";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .max(255, ValidationErrors.isLength(255))
    .required(ValidationErrors.required),
  image: Yup.mixed().when("type", {
    is: (type) => type === "image",
    then: (schema) => schema.required(ValidationErrors.required),
  }),
});

const UpdateCategoryModal = forwardRef(({ onSubmitted }, ref) => {
  const [isOpened, setIsOpened] = useState(false);
  const [category, setCategory] = useState(null);

  const form = useRef();
  const loader = useLoader();
  const dataLoading = useDataLoading();

  useImperativeHandle(ref, () => ({
    open(categoryId) {
      loadData(categoryId);
      setIsOpened(true);
    },
  }));

  const loadData = async (categoryId) => {
    dataLoading.start();

    let error = null;

    let category = await CategoriesApi.get(categoryId).catch((e) => {
      error = e;
      return null;
    });

    if (category) {
      setCategory(category);
    }

    dataLoading.stop(error);
  };

  const close = () => {
    setIsOpened(false);
  };

  const onSubmit = async (formData) => {
    loader.show();

    let error = null;
    await CategoriesApi.update(category.id, formData, true).catch((e) => {
      error = e;
      return null;
    });

    loader.hide();

    if (error) {
      ApiErrorsHelper.manage(error, form);
    } else {
      setIsOpened(false);
      onSubmitted();
    }
  };

  return (
    <Modal visible={isOpened} toggle={close} size="lg">
      <Modal.Header toggle={close}>Modifier une catégorie</Modal.Header>
      <>
        <PageDataLoader isLoading={dataLoading.isLoading}>
          <ValidationForm
            ref={form}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            initialValues={{
              name: category?.name,
              image: null,
            }}
          >
            <Modal.Body>
              <Label html="name" required>
                Nom
              </Label>
              <Input id="name" name="name" />

              <Label htmlFor="image">Image</Label>
              <ImageUploader
                name="image"
                actualUrl={category?.image}
                required
              />
            </Modal.Body>
            <Modal.Footer>
              <Button color="light" size="s" onClick={close}>
                Annuler
              </Button>
              <SubmitButton size="s" color="tertiary">
                Valider
              </SubmitButton>
            </Modal.Footer>
          </ValidationForm>
        </PageDataLoader>
      </>
    </Modal>
  );
});

export default UpdateCategoryModal;
